import type { TypographyVariantsOptions } from '@mui/material'
import { fonts } from './tokens'

/**
 * Fluid type: linear interpolation between a min size (at 360px viewport) and a
 * max size (at 1440px viewport), clamped at both ends. Keeps headings premium on
 * desktop and controlled on mobile — no layout shift, no overflow.
 */
const fluid = (minPx: number, maxPx: number, minVw = 360, maxVw = 1440): string => {
  const slope = (maxPx - minPx) / (maxVw - minVw)
  const intercept = minPx - slope * minVw
  return `clamp(${minPx}px, ${intercept.toFixed(2)}px + ${(slope * 100).toFixed(3)}vw, ${maxPx}px)`
}

/**
 * True Forge type scale — Oswald (display) · Manrope (body) · Space Grotesk (label).
 * Named scale → MUI variant mapping:
 *   Display → h1 · H1 → h2 · H2 → h3 · H3 → h4 · H4 → h5
 *   Body Large → subtitle1 · Body → body1 · Small Text → body2 / caption
 *   Buttons → button · Captions → overline
 * Oswald headings are uppercase (condensed industrial); body/labels are sentence case.
 */
export const typography: TypographyVariantsOptions = {
  fontFamily: fonts.body,
  // Display + headings — Oswald, uppercase, tight leading for a strong industrial stack.
  h1: { fontFamily: fonts.display, fontWeight: 700, lineHeight: 0.94, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: fluid(48, 92) },
  h2: { fontFamily: fonts.display, fontWeight: 600, lineHeight: 1.0, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: fluid(38, 62) },
  h3: { fontFamily: fonts.display, fontWeight: 600, lineHeight: 1.05, letterSpacing: '0.4px', textTransform: 'uppercase', fontSize: fluid(30, 46) },
  h4: { fontFamily: fonts.display, fontWeight: 600, lineHeight: 1.12, letterSpacing: '0.3px', textTransform: 'uppercase', fontSize: fluid(23, 33) },
  // Sub-headings — Space Grotesk, sentence case, for card/point titles (kept readable).
  h5: { fontFamily: fonts.label, fontWeight: 600, lineHeight: 1.3, letterSpacing: '0.1px', fontSize: '1.1875rem' },
  h6: { fontFamily: fonts.label, fontWeight: 600, lineHeight: 1.4, letterSpacing: '0.1px', fontSize: '1.0625rem' },
  // Body — Manrope, generous leading for premium readability.
  subtitle1: { fontFamily: fonts.body, fontWeight: 500, fontSize: '1.1875rem', lineHeight: 1.6 },
  subtitle2: { fontFamily: fonts.label, fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.5, letterSpacing: '0.02em' },
  body1: { fontFamily: fonts.body, fontWeight: 400, fontSize: '1.0625rem', lineHeight: 1.7 },
  body2: { fontFamily: fonts.body, fontWeight: 400, fontSize: '0.95rem', lineHeight: 1.65 },
  // Buttons — Space Grotesk, uppercase with tracking: confident + deliberate.
  button: { fontFamily: fonts.label, fontWeight: 600, fontSize: '0.875rem', letterSpacing: '0.08em', textTransform: 'uppercase' },
  overline: { fontFamily: fonts.label, fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.22em', textTransform: 'uppercase', lineHeight: 1.4 },
  caption: { fontFamily: fonts.body, fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1.5, letterSpacing: '0.2px' },
}
